import { Minus } from 'lucide-react';
import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { sound } from '@/lib/sound';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  indeterminate?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  label?: string;
}

/** Native checkbox (fast with hundreds of cells in the permission matrix), custom styled. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, indeterminate, checked, onCheckedChange, label, disabled, ...props }, ref) => (
    <label className={cn('relative inline-flex items-center gap-2', disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer', className)}>
      <input
        ref={ref}
        type="checkbox"
        className="peer absolute size-4 opacity-0"
        checked={!!checked}
        disabled={disabled}
        aria-checked={indeterminate ? 'mixed' : !!checked}
        onChange={(e) => {
          sound.tick();
          onCheckedChange?.(e.target.checked);
        }}
        {...props}
      />
      <span
        aria-hidden
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded border transition-all duration-200 ease-(--ease-spring) peer-focus-visible:ring-3 peer-focus-visible:ring-primary/25 peer-active:scale-90',
          checked || indeterminate ? 'border-primary bg-primary text-white shadow-[0_2px_6px_-1px_color-mix(in_oklab,var(--color-primary)_50%,transparent)]' : 'border-border-strong bg-surface hover:border-primary/60',
        )}
      >
        {indeterminate ? <Minus className="fx-pop size-3" strokeWidth={3} /> : checked ? (
          <svg viewBox="0 0 16 16" className="fx-check size-3" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
            <path d="M3.5 8.5l3 3 6-7" />
          </svg>
        ) : null}
      </span>
      {label && <span className="text-sm select-none">{label}</span>}
    </label>
  ),
);
Checkbox.displayName = 'Checkbox';

export function Switch({ checked, onCheckedChange, disabled, id, label }: { checked: boolean; onCheckedChange(v: boolean): void; disabled?: boolean; id?: string; label?: string }) {
  return (
    <label className={cn('inline-flex items-center gap-2.5', disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer')}>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => {
          sound.toggle(!checked);
          onCheckedChange(!checked);
        }}
        className={cn('relative h-5 w-9 rounded-full transition-colors duration-200', checked ? 'bg-primary shadow-[0_0_0_3px_color-mix(in_oklab,var(--color-primary)_15%,transparent)]' : 'bg-border-strong')}
      >
        <span className={cn('absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow transition-transform duration-200 ease-[cubic-bezier(0.3,1.4,0.5,1)]', checked && 'translate-x-4')} />
      </button>
      {label && <span className="text-sm select-none">{label}</span>}
    </label>
  );
}
